import * as React from "react";
import { Copy, Loader2 } from "lucide-react";
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@eduviaos/ui-kit";
import {
  ApiError,
  SLUG_RULES,
  boundedNameSchema,
  fieldError,
  slugProblemFor,
  type SuperAdminAssessmentTemplateRow,
} from "@eduviaos/shared";
import { deriveCloneSlug } from "@/lib/identifiers";
import { useSaCloneAssessmentTemplate } from "@/lib/saAssessmentTemplates/api";

/**
 * Phase H5c — clone an SA-visible template into a target tenant as a
 * draft. Backed by `POST /super-admin/assessment-templates/{id}/clone`
 * (Phase H5b BE).
 *
 * The SA only picks a tenant and, optionally, a new name. The clone's
 * slug is derived silently (`deriveCloneSlug`) and any slug
 * problem is reported on the name field:
 *
 *   * target_tenant_slug: 1..80, must reference a live tenant
 *   * new_slug: 1..80, /^[a-z0-9][a-z0-9-]*$/
 *   * new_name: optional, 1..200 when set; defaults to source name
 */

const NEW_NAME_MAX = 200;
const newNameSchema = boundedNameSchema(NEW_NAME_MAX, "Name");

interface TenantOption {
  slug: string;
  name: string;
  status: "active" | "suspended" | "archived";
}

export function CloneTemplateDialog({
  source,
  tenants,
  onOpenChange,
  onCloned,
}: {
  source: SuperAdminAssessmentTemplateRow | null;
  tenants: TenantOption[];
  onOpenChange: (open: boolean) => void;
  onCloned: (targetTenantName: string, clonedName: string) => void;
}) {
  const open = source !== null;

  const cloneMutation = useSaCloneAssessmentTemplate();

  // Archived tenants are hidden (the BE rejects them with 404); suspended
  // ones stay so they can be unblocked later with templates in place.
  const selectableTenants = React.useMemo(
    () => tenants.filter((t) => t.status !== "archived"),
    [tenants],
  );

  const [targetTenantSlug, setTargetTenantSlug] = React.useState("");
  const [newName, setNewName] = React.useState("");
  const [serverNameError, setServerNameError] = React.useState<string | null>(null);
  const [serverError, setServerError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (source) {
      setTargetTenantSlug(source.tenant_id);
      setNewName("");
      setServerNameError(null);
      setServerError(null);
    }
  }, [source]);

  const onTargetChange = (slug: string) => {
    setTargetTenantSlug(slug);
    setServerNameError(null);
    setServerError(null);
  };

  if (!source) {
    // Empty shell keeps a mount point for the open/close animation.
    return (
      <Dialog open={false} onOpenChange={onOpenChange}>
        <DialogContent />
      </Dialog>
    );
  }

  const targetSelected = targetTenantSlug.trim().length > 0;
  const cloningIntoSource = targetTenantSlug === source.tenant_id;
  const trimmedName = newName.trim();
  const newSlug = deriveCloneSlug(source.slug, trimmedName, cloningIntoSource);

  const nameError =
    serverNameError ??
    (trimmedName
      ? (fieldError(newNameSchema, newName) ??
        slugProblemFor(newSlug, SLUG_RULES.assessment, {
          noun: "template",
          taken: cloningIntoSource ? [source.slug] : undefined,
        }))
      : null);

  const canSubmit = targetSelected && !nameError && !cloneMutation.isPending;

  const handleSubmit = async () => {
    if (!source || !canSubmit) return;
    setServerError(null);
    try {
      const response = await cloneMutation.mutateAsync({
        template_id: source.id,
        body: {
          target_tenant_slug: targetTenantSlug,
          new_slug: newSlug,
          new_name: trimmedName || null,
        },
      });
      const targetTenantName =
        selectableTenants.find((t) => t.slug === targetTenantSlug)?.name ?? targetTenantSlug;
      onCloned(targetTenantName, response.template.name);
    } catch (raw) {
      if (raw instanceof ApiError && raw.status === 409) {
        setServerNameError(
          "The target tenant already has a template with this name. Choose a different name.",
        );
        return;
      }
      setServerError(extractCloneErrorMessage(raw));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Clone template</DialogTitle>
          <DialogDescription>
            Lands as a <code className="text-foreground">draft</code> in the target tenant — the
            source is untouched.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          <div className="border-border/60 bg-muted/40 grid gap-1 rounded-md border px-3 py-2 text-xs">
            <span className="text-muted-foreground">Source</span>
            <span className="text-foreground font-medium">{source.name}</span>
            <span className="text-muted-foreground">
              from <span className="text-foreground">{source.tenant_name ?? source.tenant_id}</span>
            </span>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="clone-target-tenant">Target tenant</Label>
            <Select value={targetTenantSlug} onValueChange={onTargetChange}>
              <SelectTrigger id="clone-target-tenant" className="h-9" aria-label="Target tenant">
                <SelectValue placeholder="Pick a tenant…" />
              </SelectTrigger>
              <SelectContent>
                {selectableTenants.map((t) => (
                  <SelectItem key={t.slug} value={t.slug}>
                    {t.name}
                    {t.slug === source.tenant_id ? (
                      <span className="text-muted-foreground ml-1.5 text-[10px]">(source)</span>
                    ) : null}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-muted-foreground text-[11px] leading-snug">
              Archived tenants are hidden — they can't receive templates.
            </span>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="clone-new-name">
              New name <span className="text-muted-foreground font-normal">(optional)</span>
            </Label>
            <Input
              id="clone-new-name"
              value={newName}
              onChange={(e) => {
                setNewName(e.target.value);
                setServerNameError(null);
                setServerError(null);
              }}
              placeholder={source.name}
              maxLength={NEW_NAME_MAX}
              autoComplete="off"
              aria-invalid={nameError ? true : undefined}
              aria-describedby="clone-new-name-help"
            />
            <span
              id="clone-new-name-help"
              className={
                nameError
                  ? "text-destructive text-[11px] leading-snug"
                  : "text-muted-foreground text-[11px] leading-snug"
              }
            >
              {nameError ??
                (cloningIntoSource
                  ? `Defaults to "${source.name}" when blank. The copy stays separate from the original.`
                  : `Defaults to "${source.name}" when blank.`)}
            </span>
          </div>

          {serverError ? (
            <div
              role="alert"
              className="bg-destructive/10 text-destructive rounded-md px-3 py-2 text-sm"
            >
              {serverError}
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={cloneMutation.isPending}>
              Cancel
            </Button>
          </DialogClose>
          <Button onClick={() => void handleSubmit()} disabled={!canSubmit}>
            {cloneMutation.isPending ? (
              <>
                <Loader2 aria-hidden="true" className="size-4 animate-spin" />
                Cloning…
              </>
            ) : (
              <>
                <Copy aria-hidden="true" className="size-4" />
                Clone template
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Map clone API errors (other than name collisions) to SA-friendly copy. */
function extractCloneErrorMessage(raw: unknown): string {
  if (raw instanceof ApiError) {
    if (raw.status === 404) {
      return (
        raw.message ||
        "Target tenant not found, or the source template was deleted while you were filling this in."
      );
    }
    if (raw.status === 400 || raw.status === 422) {
      return raw.message || "The clone request failed validation. Check the name and tenant.";
    }
    return raw.message || "Couldn't clone the template — please try again.";
  }
  if (raw instanceof Error && raw.message) return raw.message;
  return "Couldn't clone the template — please try again.";
}
